/**
 * v1.14.61.2.0 — Step 7 Riepilogo (broker-gated).
 *
 * Il cliente NON vede più il premio. Lo step mostra solo:
 * - riepilogo coperture selezionate per immobile (qualitativo)
 * - messaggio "le tue richieste saranno inviate al broker"
 * - submit → status `pending_broker_review` (no leak premio)
 */
import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send, AlertCircle, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PALETTE = {
  navy: "#0B2545",
  navyDark: "#06182E",
  gold: "#C9A961",
  goldSoft: "#E2C988",
  cream: "#F8F4ED",
  text: "#3A3A3A",
  muted: "#6E6E6E",
  border: "#E5DFD4",
  red: "#B23B3B",
};

export default function RiepilogoStep({
  token, bulk, anagrafica, onSubmitted,
}) {
  const [submitting, setSubmitting] = useState(false);
  const [submitErrors, setSubmitErrors] = useState([]);

  const buildGaranzieAttiveForImmobile = (uid) => {
    const g = [];
    if (bulk.incendio.immobili_inclusi.includes(uid)) g.push("Incendio");
    if (bulk.furto.immobili_inclusi.includes(uid)) g.push("Furto");
    if (bulk.rcf.immobili_inclusi.includes(uid)) g.push("RC Fabbricato");
    if (bulk.eventi_atmosferici.immobili_inclusi.includes(uid))
      g.push("Eventi Atmosferici");
    if (bulk.eventi_catastrofali.immobili_inclusi.includes(uid))
      g.push("Eventi Catastrofali");
    if (bulk.altre_sub_coperture.immobili_inclusi.includes(uid))
      g.push("Altre");
    return g;
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitErrors([]);
    try {
      const legacyUbicazioni = anagrafica.map((m) => {
        const uid = m.ubicazione_id;
        const inIncendio = bulk.incendio.immobili_inclusi.includes(uid);
        const inFurto = bulk.furto.immobili_inclusi.includes(uid);
        const inRcf = bulk.rcf.immobili_inclusi.includes(uid);
        const incData = bulk.incendio.per_immobile[uid] || {};
        const fuData = bulk.furto.per_immobile[uid] || {};
        const rcfData = bulk.rcf.per_immobile[uid] || {};
        const atmCells = bulk.eventi_atmosferici.sub_coperture_per_immobile;
        const catCells = bulk.eventi_catastrofali.sub_coperture_per_immobile;
        const altreCells = bulk.altre_sub_coperture.sub_coperture_per_immobile;
        return {
          ubicazione_id: uid,
          garanzie: {
            incendio: inIncendio ? {
              include: true,
              valore_fabbricato_eur: incData.valore_fabbricato_eur || 0,
              valore_contenuto_eur: incData.valore_contenuto_eur || 0,
            } : { include: false },
            furto: inFurto ? {
              include: true,
              forma: fuData.forma || "primo_rischio_assoluto",
              valore_eur: fuData.valore_eur || 0,
            } : { include: false },
            rcf: inRcf ? {
              include: true,
              codice_rischio: rcfData.codice || "",
              massimale_eur: rcfData.massimale_eur || 0,
              rc_full: !!rcfData.full,
            } : { include: false },
            vento_grandine: !!(((atmCells.I_vento_grandine || {})[uid] || {}
              ).attiva),
            sovraccarico_neve: !!(((atmCells.Z_sovraccarico_neve || {})[uid]
              || {}).attiva),
            terremoto: !!(((catCells.J_terremoto || {})[uid] || {}).attiva),
            inondazione: !!(((catCells.K_inondazione || {})[uid] || {}
              ).attiva),
            eventi_sociopolitici: !!(((altreCells.G_sociopolitici || {}
              )[uid] || {}).attiva),
            terrorismo: !!(((altreCells.H_terrorismo || {})[uid] || {}
              ).attiva),
            fenomeno_elettrico: !!((bulk.incendio.sub_coperture_globali
              .E_fenomeno_elettrico || {}).attiva),
            rottura_lastre: !!((bulk.incendio.sub_coperture_globali
              .V_rottura_lastre || {}).attiva),
          },
        };
      });
      const payload = {
        contraente: bulk.contraente,
        ubicazioni: legacyUbicazioni,
        schema_version: "bulk_v1",
        bulk_v1_snapshot: bulk,
      };
      const r = await axios.post(
        `${API}/public/quoter/invites/${token}/garanzie-submit`,
        payload,
      );
      toast.success("Richiesta inviata a Colucci Assicuratori!");
      onSubmitted && onSubmitted(r.data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (typeof detail === "object" && Array.isArray(detail.errors)) {
        setSubmitErrors(detail.errors);
        toast.error(`Validazione fallita: ${detail.errors.length} errori`);
      } else {
        toast.error(typeof detail === "string" ? detail
          : "Errore durante l'invio");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const totaleImmobiliCoperti = anagrafica.filter(
    (m) => buildGaranzieAttiveForImmobile(m.ubicazione_id).length > 0
  ).length;

  return (
    <section
      data-testid="step-riepilogo"
      className="rounded-lg p-5"
      style={{ background: "#FFF", border: `1px solid ${PALETTE.border}` }}
    >
      <h2 className="text-lg font-semibold mb-2"
           style={{ color: PALETTE.navyDark }}>
        Riepilogo delle coperture selezionate
      </h2>
      <p className="text-sm mb-4" style={{ color: PALETTE.muted }}>
        Verifica le coperture attivate per ogni immobile. Dopo l'invio,
        il tuo agente Colucci Assicuratori finalizzerà la quotazione
        personalizzata e ti contatterà con la proposta completa.
      </p>

      <div
        className="rounded p-3 mb-4 flex items-center gap-3"
        style={{ background: PALETTE.cream,
                  border: `1px solid ${PALETTE.gold}` }}
      >
        <div className="rounded-full p-2"
              style={{ background: PALETTE.gold }}>
          <Check size={18} color={PALETTE.navyDark} />
        </div>
        <div>
          <div className="text-sm font-semibold"
                style={{ color: PALETTE.navyDark }}>
            {totaleImmobiliCoperti} su {anagrafica.length} immobili con
            almeno una copertura attiva
          </div>
          <div className="text-xs" style={{ color: PALETTE.muted }}>
            Il premio finale sarà calcolato dall'agenzia.
          </div>
        </div>
      </div>

      <div
        className="overflow-x-auto rounded mb-4"
        style={{ border: `1px solid ${PALETTE.border}` }}
        data-testid="riepilogo-matrix"
      >
        <table className="w-full text-xs border-collapse">
          <thead style={{ background: PALETTE.cream }}>
            <tr>
              <th className="px-2 py-2 text-left">Immobile</th>
              <th className="px-2 py-2 text-left">Località</th>
              <th className="px-2 py-2 text-left">Coperture attivate</th>
            </tr>
          </thead>
          <tbody>
            {anagrafica.map((m) => {
              const uid = m.ubicazione_id;
              const garActive = buildGaranzieAttiveForImmobile(uid);
              return (
                <tr key={uid}
                     data-testid={`riepilogo-row-${uid}`}
                     style={{
                       background: garActive.length === 0
                         ? PALETTE.cream : "#FFF",
                     }}>
                  <td className="px-2 py-2 font-medium"
                       style={{ color: PALETTE.navyDark }}>
                    {uid}
                  </td>
                  <td className="px-2 py-2"
                       style={{ color: PALETTE.muted, fontSize: 11 }}>
                    {m.comune} ({m.provincia})
                  </td>
                  <td className="px-2 py-2"
                       style={{ color: PALETTE.text }}>
                    {garActive.length === 0 ? (
                      <span style={{ color: PALETTE.muted, fontStyle: "italic" }}>
                        Nessuna copertura selezionata
                      </span>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {garActive.map((g) => (
                          <span key={g}
                                 className="px-2 py-0.5 rounded text-[10px]"
                                 style={{
                                   background: PALETTE.goldSoft,
                                   color: PALETTE.navyDark,
                                 }}>
                            {g}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {submitErrors.length > 0 && (
        <div className="rounded p-3 mb-3 text-xs"
              style={{ background: "#FFEFEF", color: PALETTE.red,
                        border: `1px solid ${PALETTE.red}` }}
              data-testid="submit-errors-box">
          <strong>{submitErrors.length} errori di validazione:</strong>
          <ul className="mt-1 ml-4 list-disc">
            {submitErrors.slice(0, 10).map((e, i) => (
              <li key={i}>
                <strong>{e.ubicazione_id}</strong>: {e.message}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="rounded p-3 mb-4 text-xs"
            style={{ background: "#F8F4ED",
                      border: `1px dashed ${PALETTE.gold}`,
                      color: PALETTE.text }}>
        <div className="flex items-start gap-2">
          <AlertCircle size={14} color={PALETTE.gold} className="mt-0.5" />
          <div>
            <strong style={{ color: PALETTE.navyDark }}>
              Privacy & Procedura
            </strong>
            <div className="mt-1" style={{ color: PALETTE.muted }}>
              I tuoi dati saranno trasmessi al tuo agente Colucci
              Assicuratori che finalizzerà la quotazione in base alle
              tariffe Reale Mutua applicabili. La proposta completa
              (PDF + Excel + Condizioni Generali) ti sarà inviata dopo
              la revisione dell'agenzia.
            </div>
          </div>
        </div>
      </div>

      <button
        type="button"
        data-testid="wizard-submit-final"
        onClick={handleSubmit}
        disabled={submitting}
        className="w-full px-4 py-3 rounded text-sm font-semibold flex items-center justify-center gap-2 transition"
        style={{
          background: submitting ? PALETTE.muted : PALETTE.gold,
          color: PALETTE.navyDark,
        }}
      >
        {submitting ? (
          <>
            <Loader2 className="animate-spin" size={16} />
            Invio a Colucci Assicuratori in corso…
          </>
        ) : (
          <>
            <Send size={16} />
            Invia richiesta a Colucci Assicuratori
          </>
        )}
      </button>
    </section>
  );
}
